import { Button, Stack } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import type { ColumnsType } from "antd/es/table";
import Table from "antd/es/table";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import NewButton from "../../components/Buttons/NewButton";
import { Desc, Title } from "../../components/Desc";
import ProgressIndicator from "../../components/ProgressIndicator";
import strings from "../../config/localization";
import { useGetPersonnelAssessmentsOfLocationQuery } from "../../services/appln";
import { doesPermissionExist } from "../util";
import DeletePersonnelAssessment from "./DeletePersonnelAssessment";
import EditPersonnelAssessment from "./EditPersonnelAssessment";
import NewPersonnelAssessment from "./NewPersonnelAssessment";

interface PersonnelAssessment {
  key: string;
  designationId: string;
  designationName: string;
  count: string;
}

interface Data {
  rowData: PersonnelAssessment[];
  columnData: ColumnsType<PersonnelAssessment>;
}

export default function ListPersonnelAssessments() {
  const params = useParams();

  const [data, setData] = React.useState<Data>({
    rowData: [],
    columnData: [],
  });

  const navigate = useNavigate();

  const [newDialogOpen, setNewDialogOpen] = React.useState(false);

  const bandobastContext = JSON.parse(localStorage.getItem("bandobast")!);
  const timeslotContext = JSON.parse(localStorage.getItem("timeslot")!);
  const locationContext = JSON.parse(localStorage.getItem("location")!);

  const getParams = {
    bandobastId: params.bandobastId,
    timeslotId: params.timeslotId,
    locationId: params.locationId,
  };

  const columns: ColumnsType<PersonnelAssessment> = [
    {
      title: strings.designation,
      dataIndex: "designationName",
      key: "designationName",
      width: 10,
    },
    {
      title: strings.count,
      dataIndex: "count",
      key: "count",
      width: 2,
    },
    {
      title: "",
      dataIndex: "edit",
      key: "edit",
      width: 1,
      render: (text, record, index) =>
        doesPermissionExist("update-personnel-assessment") && (
          <EditPersonnelAssessment
            bandobastId={params.bandobastId!}
            timeslotId={params.timeslotId!}
            locationId={params.locationId!}
            initialValues={record}
          />
        ),
    },
    {
      title: "",
      dataIndex: "delete",
      key: "delete",
      width: 1,
      render: (text, record, index) =>
        doesPermissionExist("delete-personnel-assessment") && (
          <DeletePersonnelAssessment
            bandobastId={params.bandobastId!}
            timeslotId={params.timeslotId!}
            locationId={params.locationId!}
            personnelAssessmentId={record.key}
          />
        ),
    },
  ];

  const personnelAssessmentQueryResponse =
    useGetPersonnelAssessmentsOfLocationQuery(getParams);

  React.useEffect(() => {
    if (!personnelAssessmentQueryResponse.isSuccess) {
      return;
    }

    const rowData = personnelAssessmentQueryResponse.data.data
      .map((e: any) => {
        return {
          key: e.id,
          designationName: e.designationName,
          designationId: e.designationId,
          count: e.count,
        };
      })
      .toSorted((x: any, y: any) => {
        return x.designationName.localeCompare(y.designationName);
      });

    setData({ rowData: rowData, columnData: columns });
  }, [personnelAssessmentQueryResponse]);

  return (
    <div>
      <Box sx={{ height: 400, width: "100%" }}>
        {(personnelAssessmentQueryResponse.isLoading ||
          personnelAssessmentQueryResponse.isFetching) && <ProgressIndicator />}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Title text={strings.personnelAssessments} />
          </Grid>
          <Grid item xs={12}>
            <Stack spacing={0}>
              {/* <Desc label={strings.bandobast} value={bandobastContext.name} /> */}
              {/* <Desc label={strings.timeslot} value={timeslotContext.name} /> */}
              <Desc label={strings.location} value={locationContext.name} />
            </Stack>
          </Grid>

          <>
            <Grid item xs={2}>
              <div>
                <NewButton
                  onClickHandler={() => {
                    setNewDialogOpen(true);
                  }}
                />
                <NewPersonnelAssessment
                  bandobastId={params.bandobastId!}
                  timeslotId={params.timeslotId!}
                  locationId={params.locationId!}
                  onSubmitHandler={(newObj: any) => {
                    setNewDialogOpen(false);
                  }}
                  onCancelHandler={() => {
                    setNewDialogOpen(false);
                  }}
                  state={newDialogOpen}
                />
              </div>
            </Grid>
          </>
          <Grid item xs={12}>
            <Table
              virtual
              scroll={{ y: 600 }}
              columns={columns}
              dataSource={data.rowData}
              pagination={false}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={(e) => {
                e.preventDefault();
                navigate(-1);
              }}
            >
              {strings.back}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
}
